import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "shared/common/ContentHeaderSection";
import apiCall from "shared/services/ApiAdapter";
import useAssignments from "shared/hooks/useAssignments"; // adjust path if needed
import { QuestionMetricsPanel } from "modules/questions/QuestionMetricsPanel";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";
import masterApiCall from "shared/services/MasterDataApi";
import AssignmentFeedbackForm from "./forms/AssignmentFeedbackForm";

const AssignmentsList = ({ assignments, selectedAssignment, onSelect, loading, onApprove, sendReminderEmail, preSelectedBatchId }) => {
    const { t } = useTranslation();
    const [activeBatchId, setActiveBatchId] = useState(preSelectedBatchId || null);

    // Group assignments by batch
    const groupedByBatch = useMemo(() => {
        const groups = {};
        assignments.forEach((assignment) => {
            const batchId = assignment.batch.id;
            if (!groups[batchId]) {
                groups[batchId] = {
                    batchName: assignment.batch.batchName,
                    courseName: assignment.batch.course.courseName,
                    assignments: [],
                };
            }
            groups[batchId].assignments.push(assignment);
        });
        return groups;
    }, [assignments]);

    const batchIds = Object.keys(groupedByBatch);

    // Set default tab on first load
    useEffect(() => {
        if (preSelectedBatchId) {
            setActiveBatchId(preSelectedBatchId);
        } else if (!activeBatchId && batchIds.length > 0) {
            setActiveBatchId(batchIds[0]);
        }
    }, [batchIds, activeBatchId, preSelectedBatchId]);

    const activeAssignments =
        activeBatchId && groupedByBatch[activeBatchId]
            ? [...groupedByBatch[activeBatchId].assignments].sort((a, b) =>
                new Date(b.submittedDateTime) - new Date(a.submittedDateTime)
            )
            : [];


    return (
        <div className="w-full h-full flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
                {/* <h3 className="p-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("submittedAssignments")} ({assignments.length})
                </h3> */}

                {/* Tabs */}
                {!preSelectedBatchId && (
                    <div className="flex overflow-x-auto border-t border-gray-200 dark:border-gray-700 px-2">
                    {batchIds.map((batchId) => {
                        const { batchName, courseName } = groupedByBatch[batchId];
                        const isActive = activeBatchId === batchId;
                        return (
                            <button
                                key={batchId}
                                onClick={() => setActiveBatchId(batchId)}
                                className={`px-4 py-2 text-sm font-medium rounded-t-md whitespace-nowrap transition-colors ${isActive
                                    ? "bg-blue-600 text-white shadow"
                                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    }`}
                            >
                                {batchName} <p>[{courseName}]</p>
                            </button>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Assignment List */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <p className="p-4 text-center text-gray-500 dark:text-gray-400">{t("loading")}...</p>
                ) : activeAssignments.length === 0 ? (
                    <p className="p-4 text-center text-gray-500 dark:text-gray-400">{t("noAssignmentsFound")}</p>
                ) : (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {activeAssignments.map((assignment, index) => {
                            const isSelected = selectedAssignment?.id === assignment.id;
                            const isEven = index % 2 === 0;

                            return (
                                <li
                                    key={assignment.id}
                                    className={`grid grid-cols-12 items-center px-4 py-3 cursor-pointer transition-colors text-sm ${isSelected
                                        ? "bg-blue-100 dark:bg-blue-900/50"
                                        : isEven
                                            ? "bg-white dark:bg-gray-800"
                                            : "bg-gray-50 dark:bg-gray-700"
                                        } hover:bg-blue-50 dark:hover:bg-gray-600`}
                                >
                                    {/* Student Name */}
                                    <div className="col-span-3 text-gray-900 dark:text-gray-100 font-medium truncate">
                                        {assignment.student.name}
                                    </div>

                                    {/* Score */}
                                    <div className="col-span-2">
                                        {assignment.score != null ? (
                                            <span
                                                className={`inline-block px-2 py-1 rounded text-xs font-semibold ${assignment.score >= 80
                                                    ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200"
                                                    : assignment.score >= 50
                                                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200"
                                                        : "bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200"
                                                    }`}
                                            >
                                                {assignment.score} {t("correctAnswers")}
                                            </span>
                                        ) : (
                                            <span className="text-gray-500 dark:text-gray-400">{t("noScore")}</span>
                                        )}
                                    </div>


                                    {/* Status */}
                                    <div className="col-span-2">
                                        <span
                                            className={`inline-block px-2 py-1 rounded text-xs font-semibold ${assignment.status === "Completed"
                                                ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200"
                                                : assignment.status === "InReview"
                                                    ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200"
                                                    : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                                                }`}
                                        >
                                            {assignment.status ?? t("noStatus")}
                                        </span>
                                    </div>
                                    {/* Actions */}
                                    <div className="col-span-5 flex justify-end gap-2">
                                        {assignment?.status === "InReview" && (
                                            <button
                                                onClick={() => onApprove(assignment)}
                                                className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-700"
                                            >
                                                Approve
                                            </button>
                                        )}
                                        {assignment?.status === "Assigned" && (
                                            <button
                                                onClick={() => sendReminderEmail(assignment)}
                                                className="px-3 py-1.5 text-sm font-medium rounded-md bg-yellow-100 dark:bg-yellow-700 text-gray dark:text-white hover:bg-blue-200 dark:hover:bg-blue-600"
                                            >
                                                Send Reminder Email
                                            </button>
                                        )}
                                        {assignment?.status !== "Assigned" && (
                                            <button
                                                onClick={() => onSelect(assignment)}
                                                className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-600"
                                            >
                                                Review
                                            </button>
                                        )}
                                    </div>
                                </li>


                            );
                        })}
                    </ul>
                )}
            </div>
        </div >
    );
};

const AssessmentsContent = ({ evaluationId, batchId, displayToast, navigateTo, isEmbedded = false }) => {
    const { t } = useTranslation();
    const { startLoading, stopLoading } = useLoading();

    const [page, setPage] = useState("list");

    const [evaluation, setEvaluation] = useState(null);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [questions, setQuestions] = useState();

    const { assignments, loading: assignmentsLoading, error, refetch } = useAssignments({
        evaluationId,
        size: 1000,
    });

    const fetchEvaluation = useCallback(async () => {
        try {
            const data = await apiCall(
                `/evaluations/${evaluationId}?isActive.equals=true`,
                false,
                "GET",
                null,
                startLoading,
                stopLoading,
                displayToast
            );
            setEvaluation(data);

        } catch (err) {
            console.error("Failed to fetch evaluation:", err);
            displayToast({ type: "error", message: t("failedToLoadEvaluation") });
        }
    }, [evaluationId, startLoading, stopLoading, displayToast, t]);

    useEffect(() => {
        const init = async () => {
            if (!evaluation) return;
            let questionCodesList = evaluation?.questionIds || evaluation?.questionCodes || [];

            // normalize if it's a string
            if (typeof questionCodesList === "string") {
                questionCodesList = questionCodesList
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
            }

            // fetch questions once at init
            if (Array.isArray(questionCodesList) && questionCodesList.length > 0) {
                try {
                    const res = await masterApiCall(
                        "/questions/by-codes",
                        null,
                        "POST",
                        { questionCodes: questionCodesList },
                        startLoading,
                        stopLoading,
                        displayToast
                    );

                    const fetchedQuestionsList = Array.isArray(res)
                        ? res
                        : res?.content ?? [];

                    // Set fetched questions, this triggers the next useEffect for Firebase
                    setQuestions(fetchedQuestionsList);

                } catch (err) {
                    console.error("Error fetching questions:", err);
                    stopLoading();
                }
            }
        };
        init();
    }, [evaluation]);

    const sendReminderEmail = (assignment) => {
        apiCall("/email/AssignmentReminder/send", null, "POST",
            {
                "to": assignment.student?.email,
                "subject": `Assignment Reminder for ${assignment.evaluation?.name}`,
                "studentName": assignment.student?.name,
                "evaluationName": assignment.evaluation?.name,
                "tutorName": `${assignment.batch?.course?.tutor?.user?.firstName} ${assignment.batch?.course?.tutor?.user?.lastName}`
            },
            startLoading, stopLoading, displayToast);
    }
    const handleAssignmentClick = (assignment) => {
        // if (selectedAssignment?.id === assignment.id) return;
        setSelectedAssignment(assignment);
        // fetchQuestionMetrics(assignment);
        setPage("metrics")
    };

    const handleApproveClick = (assignment) => {
        setSelectedAssignment(assignment);
        setPage("approve");
    }

    useEffect(() => {
        if (evaluationId) fetchEvaluation();
    }, [evaluationId, fetchEvaluation]);

    if (!evaluation) {
        return (
            <div className="p-8 md:px-10 text-center text-gray-500 dark:text-gray-400">
                {t("loadingEvaluation")}...
            </div>
        );
    }

    return (
        <div className={isEmbedded ? "flex flex-col h-full" : "p-8 md:px-10"}>
            {!isEmbedded && (
                <ContentHeaderSection
                    title={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() =>
                                navigateTo("evaluations")
                            }
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
                            aria-label="Go back to batches"
                        >
                            <svg
                                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 19l-7-7 7-7"
                                ></path>
                            </svg>
                        </button>

                        <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
                            {t("assignmentsFor")} {evaluation.name}
                        </span>
                    </div>
                }
                subtitle={`Batchwise assessment details for selected evaluation`}
            />
            )}
            <div className={`relative flex transition-all duration-300 ease-in-out ${isEmbedded ? 'flex-1 h-full' : 'mt-6 h-[70vh]'}`}>
                {/* Assignments List */}
                {page === "list" && <div
                    className={`overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl shadow-md bg-white dark:bg-gray-800 w-full
                        }`}
                >
                    <AssignmentsList
                        assignments={assignments}
                        selectedAssignment={selectedAssignment}
                        onSelect={handleAssignmentClick}
                        onApprove={handleApproveClick}
                        loading={assignmentsLoading}
                        sendReminderEmail={sendReminderEmail}
                        preSelectedBatchId={batchId}
                    />
                </div>
                }

                {/* Right Drawer */}
                {page === "metrics" && (
                    <QuestionMetricsPanel assignment={selectedAssignment}
                        displayToast={displayToast}
                        questions={questions}
                        enableEditing
                        disableEditingReason="Question updates are locked because this assignment is already completed."
                        onListView={() => { setPage("list") }} />
                )}

                {/* Right Drawer */}
                {page === "approve" && (
                    <AssignmentFeedbackForm assignment={selectedAssignment}
                        questions={questions}

                        startLoading={startLoading}
                        stopLoading={stopLoading}
                        displayToast={displayToast}
                        onListView={(updatedAssignment) => {
                            refetch()
                            setPage("list")
                        }} />
                )}
            </div>

        </div>
    );
};

export default AssessmentsContent;
